import { format } from 'date-fns';
import { noop } from 'lodash';
import { createElement, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  useTrackAddedShareLinkExpirationDate,
  useTrackAddedShareLinkPasscode,
  useTrackGeneratedShareUrl,
} from '@air/analytics';
import { CustomToastItem, useToasts } from '@air/provider-toast';
import { resetSelectedItemsAction } from '@air/redux-selected-items';

import { ShareLinkActionConfirmationToastContent } from '~/components/SharePrivateCollectionModal/components/ShareLinkActionConfirmationToastContent';
import { useCreateCollectionMutation } from '~/components/SharePrivateCollectionModal/hooks/mutations/useCreateCollectionMutation';
import {
  type CollectionProps,
  type PermissionMode,
  type ShortUrlProps,
} from '~/components/SharePrivateCollectionModal/types';
import { validateShortUrlProps } from '~/components/SharePrivateCollectionModal/utils/validation';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { getFullShareUrl } from '~/utils/shareUrl';

export type UseCreateSharePrivateCollectionParams = {
  assetIds: string[];
  boardIds: string[];
  onShareLinkCreated: () => void;
  permissions: ShortUrlProps['permissions'];
};

export const DEFAULT_PERMISSIONS: ShortUrlProps['permissions'] = {
  asset: {
    canViewOriginal: false,
    canViewAllVersions: false,
    canManageDiscussions: false,
    canViewDiscussions: false,
  },
  customField: {
    canView: false,
    canManageApplications: false,
  },
  canvas: {
    canViewChatHistory: false,
  },
};

export const useCreateSharePrivateCollection = ({
  assetIds,
  boardIds,
  onShareLinkCreated,
  permissions,
}: UseCreateSharePrivateCollectionParams) => {
  const dispatch = useDispatch();
  const { trackAddedShareLinkExpirationDate } = useTrackAddedShareLinkExpirationDate();
  const { trackAddedShareLinkPasscode } = useTrackAddedShareLinkPasscode();
  const { showToast } = useToasts();
  const { currentWorkspace } = useCurrentWorkspace();
  const { trackGeneratedShareUrl } = useTrackGeneratedShareUrl();
  const createMutation = useCreateCollectionMutation();

  const [collectionProps, setCollectionProps] = useState<CollectionProps>({
    name: format(new Date(), 'MMM d, yyyy'),
  });
  const [shortUrlProps, setShortUrlProps] = useState<ShortUrlProps>({
    disabled: false,
    passcode: null,
    expirationDate: null,
    permissions,
  });

  const [validationErrors, setValidationErrors] = useState<Record<string, string> | null>(null);

  useEffect(() => {
    setCollectionProps({
      name: format(new Date(), 'MMM d, yyyy'),
    });
    setShortUrlProps({
      disabled: false,
      passcode: null,
      expirationDate: null,
      permissions,
    });
    setValidationErrors(null);
  }, [permissions]);

  useEffect(() => {
    let isCancelled = false;

    const validate = async () => {
      const validation = await validateShortUrlProps({
        name: collectionProps.name,
        disabled: shortUrlProps.disabled,
        passcode: shortUrlProps.passcode,
        expirationDate: shortUrlProps.expirationDate,
        permissions: shortUrlProps.permissions,
      });

      if (!isCancelled) {
        setValidationErrors(validation.errors);
      }
    };

    validate();

    return () => {
      isCancelled = true;
    };
  }, [shortUrlProps, collectionProps]);

  const updateCollectionName = useCallback((name: string) => {
    setCollectionProps((prev) => ({ ...prev, name }));
  }, []);

  const updateAssetPermission = useCallback(
    <K extends keyof ShortUrlProps['permissions']['asset']>(
      key: K,
      value: ShortUrlProps['permissions']['asset'][K],
    ) => {
      setShortUrlProps((prev) => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          asset: {
            ...prev.permissions.asset,
            [key]: value,
          },
        },
      }));
    },
    [],
  );

  const updateCommentPermissions = useCallback((mode: PermissionMode) => {
    const canViewDiscussions = mode === 'read-only' || mode === 'allow';
    const canManageDiscussions = mode === 'allow';

    setShortUrlProps((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        asset: {
          ...prev.permissions.asset,
          canViewDiscussions,
          canManageDiscussions,
        },
      },
    }));
  }, []);

  const updateCustomFieldPermissions = useCallback((mode: PermissionMode) => {
    const canView = mode === 'read-only' || mode === 'allow';
    const canManageApplications = mode === 'allow';

    setShortUrlProps((prev) => ({
      ...prev,
      permissions: {
        ...prev.permissions,
        customField: {
          ...prev.permissions.customField,
          canView,
          canManageApplications,
        },
      },
    }));
  }, []);

  const updatePasscode = useCallback((passcode: string | null) => {
    setShortUrlProps((prev) => ({ ...prev, passcode }));
  }, []);

  const updateExpirationDate = useCallback((expirationDate: string | null) => {
    setShortUrlProps((prev) => ({ ...prev, expirationDate }));
  }, []);

  const handleCreate = useCallback(async (): Promise<void> => {
    const validation = await validateShortUrlProps({
      name: collectionProps.name,
      disabled: shortUrlProps.disabled,
      passcode: shortUrlProps.passcode,
      expirationDate: shortUrlProps.expirationDate,
      permissions: shortUrlProps.permissions,
    });

    if (!validation.isValid) {
      setValidationErrors(validation.errors);
      return;
    }

    if (!currentWorkspace?.id) {
      throw new Error('No workspace id');
    }

    createMutation.mutate(
      {
        workspaceId: currentWorkspace.id,
        collection: {
          name: collectionProps.name,
          assetIds,
          boardIds,
        },
        shortUrl: validation.data,
      },
      {
        onSuccess: ({ shortUrl }) => {
          if (shortUrl.passcode) {
            trackAddedShareLinkPasscode({ shortId: shortUrl.id ?? '' });
          }
          if (shortUrl.expirationDate) {
            trackAddedShareLinkExpirationDate({ shortId: shortUrl.id ?? '' });
          }

          trackGeneratedShareUrl({ id: shortUrl.id, type: 'collection' });

          onShareLinkCreated();

          const shareUrl = getFullShareUrl(shortUrl.id);

          showToast(
            ({ onDismiss }) =>
              createElement(ShareLinkActionConfirmationToastContent, {
                message: 'New share link created',
                shareUrl,
                onDismiss,
              }),
            {
              timeBeforeAutoDismiss: 5_000,
              CustomContainer: CustomToastItem,
            },
          );

          dispatch(resetSelectedItemsAction());
        },
      },
    );
  }, [
    collectionProps,
    shortUrlProps,
    createMutation,
    trackAddedShareLinkPasscode,
    trackAddedShareLinkExpirationDate,
    assetIds,
    boardIds,
    onShareLinkCreated,
    showToast,
    dispatch,
    currentWorkspace?.id,
    trackGeneratedShareUrl,
  ]);

  return {
    collectionProps: {
      name: collectionProps.name,
    },
    shortUrlProps: {
      disabled: shortUrlProps.disabled,
      passcode: shortUrlProps.passcode,
      expirationDate: shortUrlProps.expirationDate,
      permissions: shortUrlProps.permissions,
    },
    isSaving: createMutation.isPending,
    validationErrors,
    updateCollectionName,
    updateLinkAccessPermission: noop,
    updateAssetPermission,
    updateCommentPermissions,
    updateCustomFieldPermissions,
    updatePasscode,
    updateExpirationDate,
    handleCreate,
    selectedItemCount: assetIds.length + boardIds.length,
  };
};
